// features/plans/components/PlansCard.tsx
"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { allPlatforms } from "../constants/platform";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  createMonitoringIcon,
  MONITORING_BADGE_STYLES,
  type MonitoringType,
} from "@/constants/monitoring";
import { useCartStore } from "../store/useCartStore";
import { toast } from "sonner";

interface PlansCardProps {
  title: string;
  price: string;
  isAdmin?: boolean;
  included?: string[];
  hasFreeTrial?: boolean;
  isPopular?: boolean;
  monitoringType?: string;
  theme?: "light" | "dark";
  disabled?: boolean;
  selectedPlatforms?: string[];
  onPlatformChange?: (platforms: string[]) => void;
}

const POPULAR_OFFSET = 48; // px — height reserved for the "MOST POPULAR" badge

export function PlansCard({
  title,
  price,
  isAdmin = false,
  included,
  hasFreeTrial = false,
  isPopular = false,
  monitoringType = "keyword",
  theme = "light",
  disabled = false,
  selectedPlatforms: externalSelectedPlatforms,
  onPlatformChange,
}: PlansCardProps) {
  const [internalSelectedPlatforms, setInternalSelectedPlatforms] = useState<
    string[]
  >([]);
  const { addItem, setIsOpen } = useCartStore();

  const isDark = theme === "dark";

  // Use external state if provided (admin mode), otherwise use internal state
  const selectedPlatforms =
    isAdmin && externalSelectedPlatforms !== undefined
      ? externalSelectedPlatforms
      : internalSelectedPlatforms;

  const togglePlatform = (id: string) => {
    const newPlatforms = selectedPlatforms.includes(id)
      ? selectedPlatforms.filter((p) => p !== id)
      : [...selectedPlatforms, id];

    if (isAdmin && onPlatformChange) {
      onPlatformChange(newPlatforms);
    } else {
      setInternalSelectedPlatforms(newPlatforms);
    }
  };

  const monitoringTypeKey = monitoringType as MonitoringType;
  const badgeColor =
    MONITORING_BADGE_STYLES[monitoringTypeKey] || "bg-blue-500";
  const monitoringIcon = createMonitoringIcon(
    monitoringTypeKey,
    "text-[16px] text-white",
  );

  const handleAddToCart = () => {
    if (selectedPlatforms.length === 0) {
      toast.error("Platform Selection Required", {
        description:
          "Please select at least one platform before adding to cart.",
      });
      return;
    }

    addItem({
      planTitle: title,
      planPrice: price,
      monitoringType: monitoringTypeKey,
      selectedPlatforms: [...selectedPlatforms],
      included: included ?? [],
      hasFreeTrial,
    });

    toast.success("Added to Cart", {
      description: `${title} plan with ${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? "s" : ""} added successfully.`,
    });

    setIsOpen(false);
    setInternalSelectedPlatforms([]);
  };

  return (
    <div
      className="relative w-[280px]"
      style={{ paddingTop: isPopular ? POPULAR_OFFSET : 0 }}
    >
      {isPopular && (
        <>
          <div
            className="bg-brand-500 absolute rounded-[16px]"
            style={{
              top: 0,
              left: -8,
              right: -8,
              bottom: -8,
            }}
          />

          <div className="absolute top-2 left-1/2 z-20 -translate-x-1/2">
            <div className="bg-brand-500 rounded-tl-[10px] rounded-tr-[10px] px-4 py-2 text-xs font-extrabold whitespace-nowrap text-neutral-50">
              MOST POPULAR
            </div>
          </div>
        </>
      )}

      <main
        className={cn(
          "relative z-10 flex w-[280px] flex-col space-y-2.5 rounded-[8px] px-6 transition-all duration-300 ease-in-out hover:shadow-md",
          included && included.length > 0 ? "h-[480px]" : "h-auto",
          isDark
            ? "bg-neutral-900 py-5 text-white"
            : "bg-white py-10 text-neutral-900",
        )}
      >
        <div className="flex-1 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3
              className={cn(
                "text-lg font-semibold",
                isDark ? "text-neutral-200" : "text-neutral-700",
              )}
            >
              {title}
            </h3>
            <div
              className={cn(
                "text-md flex h-7 w-7 items-center justify-center rounded-[8px] p-1 text-white",
                badgeColor,
                disabled ? "bg-black-300" : "",
              )}
            >
              {monitoringIcon}
            </div>
          </div>

          {/* Price */}
          <h2
            className={cn(
              "leading-tight font-bold",
              isDark
                ? "text-[24px] text-white"
                : "text-[36px] text-neutral-950",
            )}
          >
            {price}
          </h2>

          {/* Platforms */}
          {monitoringType !== "source" && (
            <div className="space-y-2">
              <h4
                className={cn(
                  "text-xs font-extrabold",
                  isDark ? "text-neutral-100" : "text-neutral-600",
                )}
              >
                SELECT PLATFORMS:
              </h4>
              <div
                className={cn(
                  "grid grid-cols-2 gap-2 rounded-[8px] p-2",
                  isDark ? "bg-[#302F2F]" : "bg-neutral-100",
                )}
              >
                {allPlatforms.map(({ id }: { id: string; src: string }) => {
                  const isSelected = selectedPlatforms.includes(id);
                  return (
                    <label
                      key={id}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => togglePlatform(id)}
                        indeterminate={disabled}
                        disabled={disabled}
                        className={
                          isDark
                            ? "data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500 disabled:bg-black-300 border-neutral-50 disabled:cursor-not-allowed"
                            : ""
                        }
                      />
                      <span
                        className={cn(
                          "text-xs leading-none font-semibold tracking-normal capitalize",
                          isDark ? "text-neutral-50" : "text-neutral-800",
                        )}
                      >
                        {id}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* INCLUDED section */}
          {included && included.length > 0 && (
            <div className="space-y-1">
              <h4
                className={cn(
                  "text-sm font-extrabold",
                  isDark ? "text-neutral-400" : "text-neutral-600",
                )}
              >
                INCLUDED
              </h4>
              <ul
                className={cn(
                  "list-disc space-y-1.5 pl-6 text-sm leading-5 font-medium -tracking-wide",
                  isDark ? "text-neutral-300" : "text-neutral-700",
                )}
              >
                {included.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="mt-auto flex flex-col gap-2">
          {hasFreeTrial && (
            <Button
              variant="minimal"
              className={cn(
                "!p-0",
                isDark
                  ? "bg-neutral-700 text-neutral-100 hover:bg-neutral-600"
                  : "bg-neutral-100 text-neutral-900",
              )}
            >
              Start 7-day free trial
            </Button>
          )}
          {!isAdmin && (
            <Button className="h-[48px] w-full" onClick={handleAddToCart}>
              Add to Cart
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
